import React, { useState, useEffect, useMemo, useContext } from 'react';
import './style.css';
import CreateCloudfrontUrl from '/src/services/CreateCloudfrontUrl';
import { Wrench, CirclePercent, ChevronLeft, Award, FlaskConical, Tag } from 'lucide-react';
import { CurlyBraceDivider } from '/src/Components/CurlyBraceDivider/CurlyBraceDivider';
import ProjectsListCard from '/src/Components/ProjectsListCard/ProjectsListCard';
import { ProjectsPayoffContext } from '../../YourOptions';
import { WindowSizeContext } from '/src/App';

const WhatWeWouldDo = ({
    handleProjectClick,
    propertyAreasWithProjects,
    projects,
}) => {

    const { deviceType } = useContext(WindowSizeContext);
    const {
        handleUserAddingSimulatorProjectId,
    } = useContext(ProjectsPayoffContext);
    // console.log('projects + propertyAreasWithProjects ', projects, propertyAreasWithProjects);

    // Image variables
    const new_hub_house_image_filename = "house-lansdcape.webp";
    const new_hub_house_image_url = CreateCloudfrontUrl(new_hub_house_image_filename);

    const [selectedArea, setSelectedArea] = useState(null);
    const [processedProjects, setProcessedProjects] = useState(null);
    const [processedSections, setProcessedSections] = useState([]);

    const [currentSectionId, setCurrentSectionId] = useState(null);
    const [currentSection, setCurrentSection] = useState(null);

    // UseEffect to process the projects and set the processedProjects state
    useEffect(() => {
        const processedProjects = processProjects();
        if (processedProjects) {
            setProcessedProjects(processedProjects);
        }
    }, [projects, propertyAreasWithProjects]);

    // UseEffect to process the projects and set the processedSections state
    useEffect(() => {
        if (processedProjects) {
            const processedProjectsBreakdown = processedProjects?.breakdown || {};

            const sectionsArray = [
                {
                    id: 'lowCost',
                    buttonText: 'Easy and Affordable Wins',
                    title: 'High Value Without Breaking the Bank',
                    description: `People usually assume that protecting their home will be expensive. But as all of these projects under $${LOW_COST_PROJECTS_THRESHOLD} show, you have a number of highly beneficial projects that are an excellent place to get started.`,
                    projects: processedProjectsBreakdown?.lowCost?.projects || [],
                    highRankNumber: processedProjectsBreakdown?.lowCost?.highRankNumber || 0,
                    houseAreas: processedProjectsBreakdown?.lowCost?.houseAreas || []
                },
                {
                    id: 'highRiskWithDiscounts',
                    buttonText: 'High Value - Known Discounts',
                    title: "Don't Just Tackle Your Highest Risk Areas, Get Rewarded for it Too",
                    description: 'These projects are high impact and have known discounts, making them easy to implement and see results quickly.',
                    projects: processedProjectsBreakdown?.highRiskWithDiscounts?.projects || [],
                    highRankNumber: processedProjectsBreakdown?.highRiskWithDiscounts?.highRankNumber || 0,
                    houseAreas: processedProjectsBreakdown?.highRiskWithDiscounts?.houseAreas || []
                },
                {
                    id: 'highRiskWithoutDiscounts',
                    buttonText: 'High Value - No Discounts Found',
                    title: 'High Value Even When No Discounts Are Available Upfront',
                    description: 'These tackle your highest risk areas, and still provide a meaningful financial benefit.',
                    projects: processedProjectsBreakdown?.highRiskWithoutDiscounts?.projects || [],
                    highRankNumber: processedProjectsBreakdown?.highRiskWithoutDiscounts?.highRankNumber || 0,
                    houseAreas: processedProjectsBreakdown?.highRiskWithoutDiscounts?.houseAreas || []
                },
                {
                    id: 'remaining',
                    buttonText: 'Lower Priority',
                    title: "Sometimes Financial Payback is Not the Highest Priority",
                    description: "These projects tackle your risks, but may not provide high levels of financial payback. Even so, they are still worth considering.",
                    projects: processedProjectsBreakdown?.remaining?.projects || [],
                    highRankNumber: processedProjectsBreakdown?.remaining?.highRankNumber || 0,
                    houseAreas: processedProjectsBreakdown?.remaining?.houseAreas || []
                }
            ]

            setProcessedSections(sectionsArray);
            setCurrentSectionId(sectionsArray[0].id);
            setCurrentSection(sectionsArray[0]);
        }
    }, [processedProjects]);

    // UseEffect to set the currentSection state when the currentSectionId changes
    useEffect(() => {
        const section = processedSections.find(section => section.id === currentSectionId);
        setCurrentSection(section);
    }, [currentSectionId]);

    // Map numeric IDs to house area classes based on the actual data
    const getAreaClass = (id) => {
        const areaMap = {
            '2': 'roof',           // First object in array has id '2'
            '1': 'landscaping',    // Second object has id '1'
            '6': 'windows',        // Third object has id '6'
            '10': 'hvac',          // Fourth object has id '10'
            '7': 'doors',          // Fifth object has id '7'
            '14': 'frame',         // Sixth object has id '14'
            '4': 'foundation',     // Seventh object has id '4'
            '9': 'siding',         // Eighth object has id '9'
            '5': 'plumbing',       // Ninth object has id '5'
            '13': 'basement'       // Tenth object has id '13'
        };
        return areaMap[id] || 'unknown';
    };

    const getAreaStats = (projectsArray, propertyAreasWithProjects) => {
        // Guard against empty arrays
        if (!projectsArray?.length || !propertyAreasWithProjects?.length) {
            return [];
        }

        // Get unique project_home_category_ids
        const uniqueCategoryIds = new Set(
            projectsArray.map(project => project.project_home_category_id)
        );

        // Create stats for each unique area
        return Array.from(uniqueCategoryIds)
            .map(categoryId => {
                // Get the matching property area
                const propertyArea = propertyAreasWithProjects.find(area => area.id === categoryId);

                // Skip if no matching property area found
                if (!propertyArea) {
                    return null;
                }

                // Get all projects for this category
                const categoryProjects = projectsArray.filter(
                    project => project.project_home_category_id === categoryId
                );

                // Get unique discount IDs across all projects in this category
                const uniqueDiscountIds = new Set(
                    categoryProjects.flatMap(project =>
                        project.project_discounts_company_specific?.map(discount => discount.id) || []
                    )
                );

                return {
                    id: propertyArea.id,
                    category: propertyArea.category,
                    risk_level_adjusted: propertyArea.risk_level_adjusted || 'unknown',
                    num_projects: categoryProjects.length,
                    num_discounts: uniqueDiscountIds.size
                };
            })
            .filter(Boolean); // Remove any null entries
    };

    const LOW_COST_PROJECTS_THRESHOLD = 300;
    const TOP_RANKED_PROJECTS_THRESHOLD = 5;

    const sortProjectsHighToLow = (projects) => {
        return projects.sort((a, b) => b.project_total_payback - a.project_total_payback);
    }


    const processProjects = () => {

        // ADD A CHECK TO RETURN NULL IF NO PROJECTS / NO PROPERTY AREAS WITH PROJECTS
        if (!projects?.length || !propertyAreasWithProjects?.length) {
            return null;
        }

        // Step 1: Add rankings based on project_total_payback (highest payback gets rank 1)
        const projectsWithRanks = [...projects]
            .sort((a, b) => b.project_total_payback - a.project_total_payback)
            .map((project, index) => ({
                ...project,
                project_total_payback_rank: index + 1
            }));

        // Step 1.5: Filter the projectsWithRanks into two new const based on whether project_total_payback / project_total_cost is greater than 0
        const projectsWithPayback = projectsWithRanks.filter(project => (project.project_total_payback / project.project_total_cost) >= 1);
        const projectsWithoutPayback = projectsWithRanks.filter(project => (project.project_total_payback / project.project_total_cost) < 1);


        // Step 2: Filter projects with total cost below LOW_COST_PROJECTS_THRESHOLD and sort it by project_total_payback_rank
        const lowCostProjects = projectsWithPayback.filter(
            project => project.project_total_cost < LOW_COST_PROJECTS_THRESHOLD
        ).sort((a, b) => a.project_total_payback_rank - b.project_total_payback_rank);

        const lowCostProjectsWithHighRankNumber = lowCostProjects.filter(
            project => project.project_total_payback_rank <= 5
        ).length;

        const lowCostProjectsHouseAreas = getAreaStats(lowCostProjects, propertyAreasWithProjects);

        // Step 3: Filter projects with cost above LOW_COST_PROJECTS_THRESHOLD and check their risk level
        const highRiskProjects = projectsWithPayback.filter(project => {
            // Only consider projects with cost >= LOW_COST_PROJECTS_THRESHOLD
            if (project.project_total_cost < LOW_COST_PROJECTS_THRESHOLD) return false;

            // Find matching property area
            const matchingArea = propertyAreasWithProjects.find(
                area => area.id === project.project_home_category_id
            );

            // Check if the matching area has high or medium risk
            if (matchingArea) {
                const riskLevel = matchingArea.risk_level_adjusted;
                return riskLevel === 'high' || riskLevel === 'medium';
            }
            return false;
        });

        // Step 4: Split high risk projects based on discounts
        const highRiskWithDiscountsProjects = highRiskProjects.filter(
            project => project.project_discounts_company_specific.length > 0
        ).sort((a, b) => a.project_total_payback_rank - b.project_total_payback_rank);

        const highRiskWithDiscountsProjectsWithHighRankNumber = highRiskWithDiscountsProjects.filter(
            project => project.project_total_payback_rank <= TOP_RANKED_PROJECTS_THRESHOLD
        ).length;

        const highRiskWithDiscountsProjectsHouseAreas = getAreaStats(highRiskWithDiscountsProjects, propertyAreasWithProjects);

        const highRiskWithoutDiscountsProjects = highRiskProjects.filter(
            project => project.project_discounts_company_specific.length === 0
        ).sort((a, b) => a.project_total_payback_rank - b.project_total_payback_rank);

        const highRiskWithoutDiscountsProjectsWithHighRankNumber = highRiskWithoutDiscountsProjects.filter(
            project => project.project_total_payback_rank <= TOP_RANKED_PROJECTS_THRESHOLD
        ).length;

        const highRiskWithoutDiscountsProjectsHouseAreas = getAreaStats(highRiskWithoutDiscountsProjects, propertyAreasWithProjects);

        // Step 5: Get remaining projects (those that don't fall into either category)
        const remainingProjects = projectsWithPayback.filter(project =>
            !lowCostProjects.includes(project) &&
            !highRiskProjects.includes(project)
        )

        // Add the projectsWithoutPayback into the remainingProjects
        const remainingProjectsWithPayback = [...remainingProjects, ...projectsWithoutPayback];

        // console.log('remainingProjectsWithPayback ', remainingProjectsWithPayback);
        // Sort the remainingProjects by project_total_payback_rank
        remainingProjectsWithPayback.sort((a, b) => a.project_total_payback_rank - b.project_total_payback_rank);

        const remainingProjectsWithHighRankNumber = remainingProjectsWithPayback.filter(
            project => project.project_total_payback_rank <= TOP_RANKED_PROJECTS_THRESHOLD
        ).length;

        const remainingProjectsHouseAreas = getAreaStats(remainingProjectsWithPayback, propertyAreasWithProjects);

        return {
            projectsWithRanks,
            breakdown: {
                lowCost: {
                    projects: lowCostProjects,
                    highRankNumber: lowCostProjectsWithHighRankNumber,
                    houseAreas: lowCostProjectsHouseAreas
                },
                highRiskWithDiscounts: {
                    projects: highRiskWithDiscountsProjects,
                    highRankNumber: highRiskWithDiscountsProjectsWithHighRankNumber,
                    houseAreas: highRiskWithDiscountsProjectsHouseAreas
                },
                highRiskWithoutDiscounts: {
                    projects: highRiskWithoutDiscountsProjects,
                    highRankNumber: highRiskWithoutDiscountsProjectsWithHighRankNumber,
                    houseAreas: highRiskWithoutDiscountsProjectsHouseAreas
                },
                remaining: {
                    projects: remainingProjectsWithPayback,
                    highRankNumber: remainingProjectsWithHighRankNumber,
                    houseAreas: remainingProjectsHouseAreas
                }
            },
        };
    };




    // const processedProjectsBreakdown = processedProjects?.breakdown || {};

    // const sectionsArray = [
    //     {
    //         id: 'lowCost',
    //         buttonText: 'Easy and Affordable Wins',
    //         title: 'High Value Without Breaking the Bank',
    //         description: `People usually assume that protecting their home will be expensive. But as all of these projects under $${LOW_COST_PROJECTS_THRESHOLD} show, you have a number of highly beneficial projects that are an excellent place to get started.`,
    //         projects: processedProjectsBreakdown?.lowCost?.projects || [],
    //         highRankNumber: processedProjectsBreakdown?.lowCost?.highRankNumber || 0,
    //         houseAreas: processedProjectsBreakdown?.lowCost?.houseAreas || []
    //     },
    //     {
    //         id: 'highRiskWithDiscounts',
    //         buttonText: 'High Value - Known Discounts',
    //         title: "Don't Just Tackle Your Highest Risk Areas, Get Rewarded for it Too",
    //         description: 'These projects are high impact and have known discounts, making them easy to implement and see results quickly.',
    //         projects: processedProjectsBreakdown?.highRiskWithDiscounts?.projects || [],
    //         highRankNumber: processedProjectsBreakdown?.highRiskWithDiscounts?.highRankNumber || 0,
    //         houseAreas: processedProjectsBreakdown?.highRiskWithDiscounts?.houseAreas || []
    //     },
    //     {
    //         id: 'highRiskWithoutDiscounts',
    //         buttonText: 'High Value - No Discounts Found',
    //         title: 'High Value Even When No Discounts Are Available Upfront',
    //         description: 'These tackle your highest risk areas, and still provide a meaningful financial benefit.',
    //         projects: processedProjectsBreakdown?.highRiskWithoutDiscounts?.projects || [],
    //         highRankNumber: processedProjectsBreakdown?.highRiskWithoutDiscounts?.highRankNumber || 0,
    //         houseAreas: processedProjectsBreakdown?.highRiskWithoutDiscounts?.houseAreas || []
    //     },
    //     {
    //         id: 'remaining',
    //         buttonText: 'Lower Priority',
    //         title: "Sometimes Financial Payback is Not the Highest Priority",
    //         description: "These projects tackle your risks, but may not provide high levels of financial payback. Even so, they are still worth considering.",
    //         projects: processedProjectsBreakdown?.remaining?.projects || [],
    //         highRankNumber: processedProjectsBreakdown?.remaining?.highRankNumber || 0,
    //         houseAreas: processedProjectsBreakdown?.remaining?.houseAreas || []
    //     }
    // ]

    // const [currentSectionId, setCurrentSectionId] = useState(sectionsArray[0].id);
    // const [currentSection, setCurrentSection] = useState(null);

    const handleSectionClick = (sectionId) => {

        if (sectionId === currentSectionId) {
            setCurrentSectionId(null);
        } else {
            setCurrentSectionId(sectionId);
        }
    }

    const handleAddingEntireSectionToSimulator = (sectionId) => {
        const section = processedSections.find(section => section.id === sectionId);
        const projectIds = section?.projects?.map(project => project.project_id) || [];

        if (projectIds.length > 0) {
            handleUserAddingSimulatorProjectId(projectIds);
        }
    }

    const handleAddingOnlyTopProjectsToSimulator = (sectionId) => {
        const section = processedSections.find(section => section.id === sectionId);
        const projectIds = section?.projects?.filter(project => project.project_total_payback_rank <= TOP_RANKED_PROJECTS_THRESHOLD).map(project => project.project_id) || [];
        if (projectIds.length > 0) {
            handleUserAddingSimulatorProjectId(projectIds);
        }
    }



    const introSentence = `We've identified ${projects?.length} recommended home improvement projects, each targeting a specific risk while providing meaningful financial benefit. It can be hard to know where to start so to help you work through this, we've bucketed the recommendations into how we'd go about tackling them.`


    const renderSectionNav = () => {
        return (
            <div className={`recommended-projects__nav-and-house-container__nav-container ${deviceType}`}>
                {processedSections.map((section) => (
                    <button
                        key={section.id}
                        className={`text-align-center recommended-projects__nav-and-house-container__nav-container__nav-button ${deviceType} ${currentSectionId === section.id ? 'selected' : 'not-selected'}`}
                        onClick={() => handleSectionClick(section.id)}
                    >
                        {section.buttonText}
                        {section.highRankNumber > 0 && (
                            <div className="nav-button-awards-container">
                                {[...Array(section.highRankNumber)].map((_, index) => (
                                    <div key={index} className="projects-list-card-high-ranked-badge small">
                                        <Award size={12} />
                                    </div>
                                ))}
                            </div>
                        )}
                    </button>
                ))}
            </div>
        )
    }


    return (
        <>
            <div className={`recommended-projects__what-we-would-do-container ${deviceType}`}>
                <p className="recommended-projects__what-we-would-do-container__intro-sentence text-align-center" dangerouslySetInnerHTML={{ __html: introSentence }} />
                <div className="recommended-projects__nav-and-house-section-frame">
                    <div className={`recommended-projects__nav-and-house-container ${deviceType}`}>
                        {deviceType !== 'desktop' && (
                            <div className={`recommended-projects__legend-container ${deviceType}`}>
                                <div className="projects-list-card-high-ranked-badge big">
                                    <Award />
                                </div>
                                <span className="recommended-projects__legend-explainer-text">Top 5 Projects Based on Total Benefit to You</span>
                            </div>
                        )}
                        {renderSectionNav()}
                        {/* {deviceType === 'desktop' && renderSectionNav()}
                        {deviceType !== 'desktop' && renderSectionNav()} */}
                        <div className="view-projects__home-hub-container recommendations-view">
                            <img
                                className="view-projects__home-hub-image"
                                src={new_hub_house_image_url}
                                alt="House diagram"
                            />
                            <div className={`view-projects__house-area-markers ${deviceType}`}>
                                {(currentSection && currentSection.houseAreas) && (
                                    currentSection?.houseAreas?.map((area) => {
                                        const areThereProjects = area.num_projects > 0;
                                        const risk_level = area.risk_level_adjusted || area.risk_level;
                                        return (
                                            <div
                                                key={area.id}
                                                className={`${areThereProjects ? 'clickable' : 'not-clickable'} house-project-marker-container house-risk-marker-${getAreaClass(area.id)} ${selectedArea?.id === area.id ? 'selected' : ''} ${risk_level} ${deviceType}`}
                                            >
                                                {deviceType === 'desktop' && (
                                                    <>

                                                        <div className={`house-risk-marker-header`}>
                                                            <span className="house-risk-marker-header-text">{area.category}</span>
                                                        </div>
                                                        {
                                                            areThereProjects && (
                                                                <div className="house-risk-marker-body">
                                                                    <Wrench size={16} />
                                                                    <span className="house-risk-marker-body-text"> {area.num_projects}</span>
                                                                    <Tag size={16} />
                                                                    <span className="house-risk-marker-body-text">{area.num_discounts}</span>
                                                                </div>
                                                            )
                                                        }
                                                    </>
                                                )}
                                            </div>
                                        )
                                    })

                                )}


                            </div>
                        </div>

                        {/* {deviceType !== 'desktop' && renderSectionNav()} */}
                    </div>
                    {deviceType === 'desktop' && (
                        <div className={`recommended-projects__legend-container ${deviceType}`}>
                            <div className="projects-list-card-high-ranked-badge big">
                                <Award />
                            </div>
                            <span className="recommended-projects__legend-explainer-text">Top 5 Projects Based on Total Benefit to You</span>
                        </div>
                    )}
                </div>
                <div className="recommended-projects__text-container">
                    <h3 className="recommended-projects__text-container__title text-align-center">{currentSection?.title}</h3>
                    <p className="recommended-projects__text-container__description text-align-center">{currentSection?.description} {currentSection?.projects?.length === 0 && ` It looks like for this home there are no projects that fit into this category.`}</p>
                </div>
                <div className="recommended-projects__section-projects-container">
                    <div className={`selected-area-details__projects-list ${deviceType}`}>

                        {currentSection?.projects?.map((project) => {

                            const isHighRanked = project.project_total_payback_rank <= TOP_RANKED_PROJECTS_THRESHOLD;
                            return (
                                <ProjectsListCard
                                    key={project.project_id}
                                    project={project}
                                    handleProjectClick={handleProjectClick}
                                    isHighRanked={isHighRanked}
                                    allowDragging={true}
                                />
                            )
                        })}
                    </div>
                    <div className={`recommended-projects__section-projects-add-all-to-simulator-buttons-container ${deviceType}`}>
                        {currentSection?.highRankNumber > 0 && (
                            <button
                                className="recommended-projects__section-projects-add-to-simulator-button top-projects"
                                onClick={() => handleAddingOnlyTopProjectsToSimulator(currentSectionId)}
                            >
                                <FlaskConical />
                                Add Top Projects to Combo Lab
                            </button>

                        )}
                        {currentSection?.projects?.length > 0 && (
                            <button
                                className="recommended-projects__section-projects-add-to-simulator-button all-projects"
                                onClick={() => handleAddingEntireSectionToSimulator(currentSectionId)}
                            >
                                <FlaskConical />

                                Add All to Combo Lab
                            </button>
                        )}
                    </div>
                </div>

            </div >

        </>
    );
};

export default WhatWeWouldDo;